import { Stack, useTheme } from "@mui/material";
import type { FC } from "react";
import { AdvisorConfirmDrawer } from "web_myplann/Advisor/Components/AdvisorConfirmDrawer";
import { AdvisorPendingPickerList } from "web_myplann/Advisor/Components/AdvisorPendingPickerList";
import { AdvisorPickerList } from "web_myplann/Advisor/Components/AdvisorPickerList";
import { useAdvisorPendingActions } from "web_myplann/Advisor/Hooks/useAdvisorPendingActions";
import type { AdvisorConfirmCopy } from "web_myplann/Advisor/Types/AdvisorConfirmCopy";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import type { AgentAssignedListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { SectionHeader } from "web_myplann/Shared/Components/SectionHeader";

const R = Resources.Advisor;

const refuseCopy: AdvisorConfirmCopy = {
	title: R.refuseConfirm.title,
	description: R.refuseConfirm.description,
	confirmLabel: R.refuseConfirm.confirm,
};

type Props = {
	open: boolean;
	onClose: () => void;
	advisors: AgentAssignedListItemResponseDTO[];
	pendingAdvisors: AgentAssignedListItemResponseDTO[];
	pendingCount: number;
	selectedId?: string;
	onSelect: (uid: string) => void;
};

export const AdvisorPickerDrawer: FC<Props> = ({
	open,
	onClose,
	advisors,
	pendingAdvisors,
	pendingCount,
	selectedId,
	onSelect,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const {
		refusedAdvisor,
		approvingIds,
		isRefusing,
		approve,
		requestRefuse,
		dismissRefuse,
		confirmRefuse,
	} = useAdvisorPendingActions();
	const hasGoalsRight = useGoalsRightOptional();

	const selectAdvisor = (uid: string) => {
		onSelect(uid);
		onClose();
	};

	return (
		<>
			<MyPlannDrawer
				open={open && !refusedAdvisor}
				onClose={onClose}
				grabber={hasGoalsRight}
				closeButton={!hasGoalsRight}
				title={t(R.pickerTitle)}
				paperSx={{ bgcolor: theme.palette.background.paper }}
			>
				<Stack pb={theme.spacingTokens.xl}>
					{pendingAdvisors.length > 0 && (
						<>
							<SectionHeader
								title={t(R.pendingHeading, { count: pendingCount })}
								compactTop
							/>
							<AdvisorPendingPickerList
								advisors={pendingAdvisors}
								approvingIds={approvingIds}
								onApprove={approve}
								onRefuse={requestRefuse}
							/>
							<SectionHeader title={t(R.connectedHeading)} />
						</>
					)}
					<AdvisorPickerList
						advisors={advisors}
						selectedId={selectedId}
						onSelect={selectAdvisor}
					/>
				</Stack>
			</MyPlannDrawer>

			<AdvisorConfirmDrawer
				open={Boolean(refusedAdvisor)}
				copy={refuseCopy}
				advisorName={refusedAdvisor?.name ?? ""}
				loading={isRefusing}
				onClose={dismissRefuse}
				onConfirm={confirmRefuse}
			/>
		</>
	);
};
